/**
 * Mini lessons content registry.
 * Add video paths, descriptions, and other content here as we expand.
 * Video paths are relative to /public (e.g. /videos/symvol/...)
 */
export interface MiniLessonSection {
  title: string;
  intro?: string;
  items?: string[];
  subsections?: Array<{ title: string; text: string }>;
}

export interface LessonOutlineSection {
  number: number;
  title: string;
  duration?: string;
  parts?: Array<{ label?: string; text: string }>;
}

/** Read-aloud script line — teacher dialogue or stage direction */
export interface ReadAloudLine {
  kind: "teacher" | "direction";
  text: string;
}

export interface MiniLesson {
  id: string;
  title: string;
  /** Optional video path (relative to public, e.g. /videos/symvol/...) */
  video?: string;
  /** Optional short description/overview */
  description?: string;
  /** Optional duration (e.g. "15 min") */
  duration?: string;
  /** Structured content sections — rendered below video */
  content?: {
    gradeLevel?: string;
    estimatedTime?: string;
    purpose?: string;
    keyConcepts?: string[];
    lessonOutline?: LessonOutlineSection[];
    preparationMaterials?: string[];
    readAloudScript?: ReadAloudLine[];
    /** Legacy: overview, sections */
    overview?: string;
    sections?: MiniLessonSection[];
  };
  /** Extensible: add more fields as needed */
  [key: string]: unknown;
}

export const MINI_LESSONS: MiniLesson[] = [
  {
    id: "1",
    title: "The Three Brain Parts",
    video: "/videos/symvol/symvol_three_brain_parts_v5.mp4",
    duration: "15 min",
    content: {
      gradeLevel: "K-8 (Universal)",
      estimatedTime: "15 minutes",
      purpose:
        "To teach students how the brain works so they can calm emotions, think clearly, and feel safe.",
      keyConcepts: [
        "Amygdala: The Security Guard",
        "Hippocampus: The Memory Maker",
        "Prefrontal Cortex: The Wise Leader",
      ],
      lessonOutline: [
        {
          number: 1,
          title: "Introduction",
          duration: "2 mins",
          parts: [
            {
              label: "Ask",
              text: '"Ever wonder why you sometimes can\'t seem to think straight when you\'re upset?"',
            },
            {
              label: "Explain",
              text: "The brain coordinates three fundamental capacities: thinking, feeling, and acting.",
            },
          ],
        },
        {
          number: 2,
          title: "Meet the Three Brain Parts",
          duration: "5 mins",
          parts: [
            {
              label: "The Amygdala (Security Guard)",
              text: "Constantly watches for danger with protective vigilance. It senses threats and triggers fight, flight, or freeze responses to keep you safe.",
            },
            {
              label: "The Hippocampus (Memory Maker)",
              text: "Stores experiences through experience storage. It helps retain learning and records emotions so you can remember them later.",
            },
            {
              label: "The Prefrontal Cortex (Wise Leader)",
              text: "Provides executive guidance for complex thinking. It helps you think clearly, make good decisions, solve problems, and show empathy.",
            },
          ],
        },
        {
          number: 3,
          title: "The Problem: Emotional Overwhelm",
          duration: "3 mins",
          parts: [
            {
              label: "Explain",
              text: "When overwhelmed, the amygdala sounds the alarm and the prefrontal cortex goes offline.",
            },
            {
              label: "The Science",
              text: "The brain detects a threat, shifts resources toward protection, and prioritizes safety over rational thought.",
            },
            {
              label: "The Result",
              text: "Big emotions work against new learning. Explaining feelings, making choices, focusing, and learning become difficult tasks.",
            },
          ],
        },
        {
          number: 4,
          title: "The Solution: The 4-Step Process",
          duration: "5 minutes",
          parts: [
            {
              label: "Explain",
              text: "The brain is changeable! You can develop new patterns with practice to calm the amygdala and reactivate the prefrontal cortex. Practice the 4 Steps together:",
            },
            {
              label: "PAUSE",
              text: "Notice what's happening inside (body awareness connects to brain recognition). Say to yourself: \"My amygdala is activated.\"",
            },
            {
              label: "BREATHE",
              text: "Take a Brain Break. Breathe in slowly through the nose, out longer through the mouth (repeat 3-5 times). This sends a safety signal to the brain.",
            },
            {
              label: "NAME THE FEELING",
              text: "Say: \"I feel frustrated,\" or \"I feel worried.\" Named emotions have less power. Naming it calms the amygdala and helps the hippocampus organize the experience.",
            },
            {
              label: "REENGAGE",
              text: "Notice the calm. The prefrontal cortex is reengaged, allowing clear thinking and thoughtful responses instead of impulsive reactions.",
            },
          ],
        },
      ],
    },
  },
  {
    id: "2",
    title: "The Brain Break",
    duration: "3-5 minutes",
    content: {
      gradeLevel: "K-8 (Universal)",
      estimatedTime: "3-5 minutes",
      purpose:
        "To guide students through the core mindful breathing practice to relax the mind and focus on the present.",
      preparationMaterials: [
        "A resonant chime (physical or digital).",
        "Ensure students have a comfortable place to sit (in a chair or on the floor).",
      ],
      readAloudScript: [
        {
          kind: "teacher",
          text: "Let's do a brain break. Here's how it goes. Oh, find a comfortable place to sit, in your chair, on the floor. Sit up tall. Breathe in so the air can fill your lungs.",
        },
        {
          kind: "teacher",
          text: "I invite you to close your eyes, but you don't have to.",
        },
        {
          kind: "teacher",
          text: "Now I'm gonna ring this chime. Listen for as long as you can.",
        },
        { kind: "direction", text: "[Ring the chime] *" },
        {
          kind: "teacher",
          text: "And then when you can't hear it any longer, just relax. Turn your hands over. Open your palms and just slowly breathe.",
        },
        { kind: "direction", text: "[Pause for breathing] *" },
        {
          kind: "teacher",
          text: "As you breathe, feel the air going into your lungs, into your tummy, and then let it out very gently.",
        },
        {
          kind: "teacher",
          text: "Your mind, your beautiful mind, it's going to think of a lot of things 'cause that's what minds do. But we want to relax our mind. So just take those thoughts, pretend they're clouds, and watch them drift away.",
        },
        {
          kind: "teacher",
          text: "And come back to your breath, your wonderful, calming breath.",
        },
        { kind: "direction", text: "[Pause for breathing] *" },
        {
          kind: "teacher",
          text: "When I ring the chime again, you can slowly open your eyes. Wiggle around, and feel grateful for this day.",
        },
        { kind: "direction", text: "[Ring the chime again] *" },
      ],
    },
  },
  {
    id: "3",
    title: "The Power of Gratitude",
    video: "/videos/symvol/symvol_power_of_gratitude.mp4",
    duration: "10-15 min",
    description:
      "Explore what gratitude means, how it affects the brain, and practice expressing it to build resilience and optimism.",
    content: {
      gradeLevel: "K-8 (Universal/Adaptable)",
      estimatedTime: "10-15 minutes",
      purpose:
        "To explore what gratitude means, understand how it positively impacts the brain, and practice expressing it to build emotional resilience and optimism.",
      keyConcepts: [
        "The Science: Gratitude triggers the release of dopamine and serotonin.",
        "The Shift: It gently shifts the brain away from worry and frustration toward appreciation and hope.",
        "The Muscle: Gratitude is like a muscle; the more you practice it, the stronger and happier the mind becomes.",
      ],
      preparationMaterials: [
        "For teacher: gratitude stone or object; poster paper and markers for class gratitude poster.",
        "For students: pen and paper or gratitude journal.",
      ],
      lessonOutline: [
        {
          number: 1,
          title: "Introduction: The Science of Gratitude",
          duration: "3 mins",
          parts: [
            {
              label: "Explain",
              text: "Gratitude is more than just saying thanks. It actually triggers the brain to release feel-good chemicals like dopamine and serotonin.",
            },
            {
              label: "The Impact",
              text: "These chemicals work together to help you feel calmer, happier, and more connected to each other. It reduces stress, enhances focus, and builds emotional resilience.",
            },
          ],
        },
        {
          number: 2,
          title: "What Can We Be Grateful For?",
          duration: "3 mins",
          parts: [
            {
              label: "Explain",
              text: "Gratitude doesn't have to be about grand gestures or monumental achievements.",
            },
            {
              label: "Provide Examples",
              text: "It can be simple things: a friend's laughter, a favorite song, a delicious meal, a pet, a sunny day, a cozy sweater, good sleep, a small win, or a kind word.",
            },
            {
              label: "Remind Students",
              text: "Every person's gratitude is unique and personal. There are no right or wrong answers.",
            },
          ],
        },
        {
          number: 3,
          title: "Gratitude Practice",
          duration: "5-8 mins",
          parts: [
            {
              label: "Explain",
              text: "Choose ONE of the following activities based on your students' age level:",
            },
            {
              label: "Option A: Gratitude Circle (All Ages)",
              text: "Gather students in a circle and introduce a special gratitude stone or object. Pass it around and let each student share one thing they are grateful for. If a student can't think of anything, encourage them to simply smile and pass the object without any pressure.",
            },
            {
              label: "Option B: Gratitude Poster (All Ages)",
              text: "Work together as a class to create a gratitude poster. Invite students to add words or drawings of things they appreciate. Display it visibly to serve as a constant reminder.",
            },
            {
              label: "Option C: Gratitude Journals & Letters (Older Students)",
              text: "Have students use a gratitude journal to write personal letters or notes to people they are grateful for (parents, friends, coaches). Prompt them to explain why that person matters, which helps build deeper connections and self-awareness.",
            },
          ],
        },
        {
          number: 4,
          title: "Wrap-Up",
          duration: "1 min",
          parts: [
            {
              label: "Explain",
              text: "Gratitude trains the brain to notice what's going well instead of only focusing on difficulties.",
            },
            {
              label: "Remind Students",
              text: "It is a small daily habit that yields profound lifelong benefits. Remember to think of something you are grateful for every day!",
            },
          ],
        },
      ],
    },
  },
  {
    id: "4",
    title: "The Practice of Happiness (Combines Happiness I & II)",
    video: "/videos/symvol/symvol_practice_of_happiness.mp4",
    duration: "10-15 min",
    description:
      "Learn that happiness is a practice you can build, and try daily habits that boost focus, resilience, and connection.",
    content: {
      gradeLevel: "K-2",
      estimatedTime: "10-15 minutes",
      purpose:
        "To understand that happiness is a practice we can build like a muscle, and to introduce daily habits that increase focus, resilience, and connection.",
      keyConcepts: [
        "Happiness is a Muscle: It isn't about \"being positive all the time.\" It is a practice you build a little bit every day.",
        "The Science: Positive practices release dopamine and endorphins, which support mood, motivation, and learning. Repeated positive experiences actually rewire the brain for optimism.",
        "The Tools: Perspective shifting, movement, and forgiveness free up mental energy and build emotional flexibility.",
      ],
      preparationMaterials: [
        "For teacher: paper flowers or leaves, tape or pushpins for the Gratitude Garden.",
        "For students: crayons or markers.",
      ],
      lessonOutline: [
        {
          number: 1,
          title: "Introduction: The Science of Happiness",
          duration: "3 mins",
          parts: [
            {
              label: "Explain",
              text: "Happiness is a skill we can practice. When we pause to breathe, move, or show gratitude, our brains release feel-good chemicals (dopamine and endorphins).",
            },
            {
              label: "The Impact",
              text: "These chemicals help us focus and learn. When we practice noticing what is working instead of fixating on what is not, we literally rewire our brains to be more resilient.",
            },
          ],
        },
        {
          number: 2,
          title: "Happiness Practices",
          duration: "7-10 mins",
          parts: [
            {
              label: "Explain",
              text: "Happiness is felt in the body and learned through experience. Introduce one or more of the following practices to the class today, and turn them into ongoing habits:",
            },
            {
              label: "The Happiness Check-In",
              text: "Start the day with three slow breaths, eyes closed. Ask students to point to where in their body they feel good (a warm tummy, relaxed shoulders, a smile forming). This connects feelings to physical awareness.",
            },
            {
              label: "The Smile Experiment",
              text: "Have students hold a big, silly smile for 30 seconds—even if they don't feel like it. Then ask: Did anything change? The physical act of smiling sends signals to the brain that shift mood.",
            },
            {
              label: "The Gratitude Garden",
              text: "Build a garden on a classroom wall. Each day, have students add one thing they are thankful for on a paper flower or leaf. Every Friday, step back and admire how much good you've noticed together.",
            },
            {
              label: "One-Minute Joy Break",
              text: "When energy dips, lead a 1-minute break to shake it out, jump, or dance. Then ask students to notice how their body feels. This teaches them they can shift their own mood.",
            },
          ],
        },
        {
          number: 3,
          title: "Wrap-Up",
          duration: "2 mins",
          parts: [
            {
              label: "Explain",
              text: "Happiness isn't something you learn once and move on. It's a thread you weave through every day—how you start the morning, handle setbacks, and close the day.",
            },
            {
              label: "Remind Students",
              text: "Every time you take a deep breath, celebrate a small win, or let go of a grudge, you are strengthening your brain!",
            },
          ],
        },
      ],
    },
  },
  {
    id: "5",
    title: "Acts of Kindness",
    video: "/videos/symvol/symvol_acts_of_kindness.mp4",
    duration: "10-15 min",
    description:
      "See kindness as a learnable skill that changes the brain, and try habits that build empathy, lower stress, and strengthen community.",
    content: {
      gradeLevel: "K-2",
      estimatedTime: "10-15 minutes",
      purpose:
        "To understand that kindness is a learnable skill that changes the brain, and to introduce daily habits that build empathy, lower stress, and strengthen the classroom community.",
      keyConcepts: [
        "Kindness is a Mindful Choice: It is not just a personality trait you either have or don't have. It is a deliberate decision to act with care, empathy, and respect, and a learnable skill you can practice and strengthen.",
        "The Science: When students perform acts of kindness, their brains release dopamine, benefiting both the giver and the receiver.",
        "Neural Pathways: Repeated practice builds neural pathways that strengthen empathy and sensitivity to others' needs. It lowers stress, supports focus, and strengthens connections.",
      ],
      preparationMaterials: [
        "For students: paper, crayons, or markers for drawing and writing.",
      ],
      lessonOutline: [
        {
          number: 1,
          title: "Introduction: The Science of Kindness",
          duration: "3 mins",
          parts: [
            {
              label: "Explain",
              text: "Kindness is a powerful tool and a learnable skill. When we make the mindful choice to act with care, our brains release a feel-good chemical called dopamine.",
            },
            {
              label: "The Impact",
              text: "This dopamine boost helps both the person giving the kindness and the person receiving it! Every time we choose to be kind, we are building neural pathways in our brain that lower stress and support our focus.",
            },
          ],
        },
        {
          number: 2,
          title: "Kindness Practices",
          duration: "7-10 mins",
          parts: [
            {
              label: "Explain",
              text: "Kindness is learned through imitation and play. Introduce one or more of the following challenges to the class today to turn kindness into an ongoing habit:",
            },
            {
              label: "Share and Smile",
              text: "Go around the room and ask each student to raise their hand and share one thing they could do today to make someone smile. Challenge them to actually do it before the day ends.",
            },
            {
              label: "The \"True and Specific\" Challenge",
              text: "Pair students up and give them one simple challenge: say something kind to your partner that is true and specific. Not just \"you're nice\"—guide them to say something they really mean.",
            },
            {
              label: "Draw and Celebrate",
              text: "At the end of the day, ask students to draw or write about one kind thing they did or saw. Make it a visible, celebrated moment in the classroom.",
            },
          ],
        },
        {
          number: 3,
          title: "Wrap-Up",
          duration: "2 mins",
          parts: [
            {
              label: "Explain",
              text: "Kindness isn't a one-time activity; it's an ongoing habit. Every kind choice you make strengthens your brain and your community.",
            },
            {
              label: "Remind Students",
              text: "Your role is to make the invisible visible. Small actions have real, powerful effects!",
            },
          ],
        },
      ],
    },
  },
];

export function getMiniLessonById(id: string): MiniLesson | undefined {
  return MINI_LESSONS.find((m) => m.id === id);
}
